import { Label, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Slider, Textarea } from '@creanizer/ui'
import { Shield, Coins } from 'lucide-react'
import type { SocietyInput } from '@creanizer/types'

interface DetailedModeFormProps {
  input: SocietyInput
  onChange: (input: SocietyInput) => void
}

export function DetailedModeForm({ input, onChange }: DetailedModeFormProps) {
  const handleChange = (field: keyof SocietyInput, value: any) => {
    onChange({ ...input, [field]: value })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">組織名（省略可）</Label>
          <Input
            id="name"
            value={input.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="AIが自動生成します"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="societyType">組織タイプ</Label>
          <Select value={input.societyType} onValueChange={(value) => handleChange('societyType', value)}>
            <SelectTrigger id="societyType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="government">政府・行政機関</SelectItem>
              <SelectItem value="military">軍事組織</SelectItem>
              <SelectItem value="mercenary">傭兵団</SelectItem>
              <SelectItem value="guild">ギルド・組合</SelectItem>
              <SelectItem value="merchant">商業組織</SelectItem>
              <SelectItem value="criminal">犯罪組織</SelectItem>
              <SelectItem value="religious">宗教組織</SelectItem>
              <SelectItem value="academic">学術機関</SelectItem>
              <SelectItem value="secret_society">秘密結社</SelectItem>
              <SelectItem value="adventurer_party">冒険者パーティー</SelectItem>
              <SelectItem value="noble_house">貴族家</SelectItem>
              <SelectItem value="cult">カルト教団</SelectItem>
              <SelectItem value="rebel">反乱軍・レジスタンス</SelectItem>
              <SelectItem value="group">グループ</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {input.societyType === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="customType">カスタムタイプ</Label>
          <Input
            id="customType"
            value={input.customType || ''}
            onChange={(e) => handleChange('customType', e.target.value)}
            placeholder="組織の種類を入力"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="scale">組織規模</Label>
          <Select value={input.scale} onValueChange={(value) => handleChange('scale', value)}>
            <SelectTrigger id="scale">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">小規模（10人未満）</SelectItem>
              <SelectItem value="medium">中規模（10-100人）</SelectItem>
              <SelectItem value="large">大規模（100-1000人）</SelectItem>
              <SelectItem value="massive">巨大（1000人以上）</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="genre">ジャンル</Label>
          <Select value={input.genre} onValueChange={(value) => handleChange('genre', value)}>
            <SelectTrigger id="genre">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fantasy">ファンタジー</SelectItem>
              <SelectItem value="sf">SF</SelectItem>
              <SelectItem value="modern">現代</SelectItem>
              <SelectItem value="post_apocalypse">ポストアポカリプス</SelectItem>
              <SelectItem value="cyberpunk">サイバーパンク</SelectItem>
              <SelectItem value="steampunk">スチームパンク</SelectItem>
              <SelectItem value="mythology">神話</SelectItem>
              <SelectItem value="horror">ホラー</SelectItem>
              <SelectItem value="urban_fantasy">アーバンファンタジー</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {input.genre === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="customGenre">カスタムジャンル</Label>
          <Input
            id="customGenre"
            value={input.customGenre || ''}
            onChange={(e) => handleChange('customGenre', e.target.value)}
            placeholder="ジャンルを入力"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="purpose">組織の目的・理念</Label>
        <Textarea
          id="purpose"
          value={input.purpose || ''}
          onChange={(e) => handleChange('purpose', e.target.value)}
          placeholder="組織が目指す目標や理念を詳細に記述"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="alignment" className="flex items-center gap-2">
          <Shield className="h-4 w-4" />
          属性・思想
        </Label>
        <Select value={input.alignment} onValueChange={(value) => handleChange('alignment', value)}>
          <SelectTrigger id="alignment">
            <SelectValue placeholder="選択してください" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lawful_good">秩序・善</SelectItem>
            <SelectItem value="neutral_good">中立・善</SelectItem>
            <SelectItem value="chaotic_good">混沌・善</SelectItem>
            <SelectItem value="lawful_neutral">秩序・中立</SelectItem>
            <SelectItem value="true_neutral">真なる中立</SelectItem>
            <SelectItem value="chaotic_neutral">混沌・中立</SelectItem>
            <SelectItem value="lawful_evil">秩序・悪</SelectItem>
            <SelectItem value="neutral_evil">中立・悪</SelectItem>
            <SelectItem value="chaotic_evil">混沌・悪</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="influence">影響力: {input.influence || 5}</Label>
          <Slider
            id="influence"
            min={1}
            max={10}
            step={1}
            value={[input.influence || 5]}
            onValueChange={(value) => handleChange('influence', value[0])}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="secrecy">秘密性: {input.secrecy || 3}</Label>
          <Slider
            id="secrecy"
            min={1}
            max={10}
            step={1}
            value={[input.secrecy || 3]}
            onValueChange={(value) => handleChange('secrecy', value[0])}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="resources" className="flex items-center gap-2">
            <Coins className="h-4 w-4" />
            資源
          </Label>
          <Select value={input.resources} onValueChange={(value) => handleChange('resources', value)}>
            <SelectTrigger id="resources">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="poor">貧困</SelectItem>
              <SelectItem value="modest">質素</SelectItem>
              <SelectItem value="adequate">適度</SelectItem>
              <SelectItem value="wealthy">裕福</SelectItem>
              <SelectItem value="vast">莫大</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="primaryActivity">主要活動</Label>
        <Input
          id="primaryActivity"
          value={input.primaryActivity || ''}
          onChange={(e) => handleChange('primaryActivity', e.target.value)}
          placeholder="組織の主な活動内容"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="operationalArea">活動地域</Label>
        <Input
          id="operationalArea"
          value={input.operationalArea || ''}
          onChange={(e) => handleChange('operationalArea', e.target.value)}
          placeholder="組織が活動する地域や範囲"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="additionalSettings">追加設定（任意）</Label>
        <Textarea
          id="additionalSettings"
          value={input.additionalSettings || ''}
          onChange={(e) => handleChange('additionalSettings', e.target.value)}
          placeholder="その他の詳細な設定や要望"
          rows={3}
        />
      </div>
    </div>
  )
}